<template>
  <!-- member incoming video call meeting modal ringing -->
  <TransitionRoot as="template" :show="props.open">
    <Dialog
      as="div"
      class="fixed z-50 w-screen h-screen overflow-hidden top-0 left-0"
      @close="closeModal"
      :open="props.open"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-70 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex justify-center p-4 text-center mt-36 sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6"
            >
              <div class="">
                <!-- Avatar image with separate glowing ring -->
                <div
                  class="mt-5 sm:mt-8 mx-auto relative flex justify-center items-center"
                >
                  <!-- Glowing ring using a pseudo-element -->
                  <div
                    class="absolute w-[calc(20%+1rem)] h-[calc(70%+1rem)] rounded-full bg-green-400 opacity-50 animate-ping"
                  ></div>

                  <!-- Avatar image -->
                  <img
                    class="w-[250px] lg:px-10 mx-auto rounded-full z-10"
                    src="@/assets/media/images/default-avatar.png"
                    alt=""
                  />
                </div>

                <div class="mt-3 sm:mt-5 text-center">
                  <DialogTitle
                    as="h3"
                    class="text-lg font-semibold leading-6 text-gray-900"
                    >Awesome Creator is Calling You!
                  </DialogTitle>

                  <!-- start/end call buttons -->
                  <div
                    class="w-full mt-5 text-center flex gap-4 relative mx-auto justify-center"
                  >
                    <!-- start call button -->
                    <div
                      @mouseenter="showVideoCallStartCallTooltip = true"
                      @mouseleave="showVideoCallStartCallTooltip = false"
                      @click="handleAnswerCall"
                      class="rounded-full w-12 h-10 bg-green-500 hover:bg-green-400 flex items-center justify-center cursor-pointer"
                    >
                      <PhoneIcon
                        class="w-6 h-6 text-zinc-200 font-bold transform rotate-[345deg]"
                      ></PhoneIcon>

                      <!-- start video call tooltip -->
                      <div
                        v-if="showVideoCallStartCallTooltip"
                        class="absolute text-center w-24 bottom-full p-2 -translate-x-2 mb-3 bg-zinc-800 opacity-90 text-white text-sm rounded tooltip z-10"
                      >
                        Answer Call
                        <div
                          class="tooltip-arrow absolute left-1/2 -right-1 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-zinc-800 opacity-90"
                        ></div>
                      </div>
                    </div>

                    <!-- end call button -->
                    <div
                      @mouseenter="showVideoCallEndCallTooltip = true"
                      @mouseleave="showVideoCallEndCallTooltip = false"
                      @click="closeModal"
                      class="rounded-full w-12 h-10 pt-1 bg-red-500 hover:bg-red-400 flex items-center justify-center cursor-pointer"
                    >
                      <PhoneIcon
                        class="w-6 h-6 text-zinc-200 font-bold transform rotate-[135deg]"
                      ></PhoneIcon>

                      <!-- end video call tooltip -->
                      <div
                        v-if="showVideoCallEndCallTooltip"
                        class="absolute text-center w-20 bottom-full p-2 -translate-x-2 mb-3 bg-zinc-800 opacity-90 text-white text-sm rounded tooltip z-10"
                      >
                        Reject Call
                        <div
                          class="tooltip-arrow absolute left-1/2 -right-1 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-zinc-800 opacity-90"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import {
  defineProps,
  defineEmits,
  onMounted,
  ref,
  onBeforeUnmount,
  // watch,
  watchEffect
} from 'vue';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue';
import {
  // InformationCircleIcon,
  // CheckIcon,
  PhoneIcon
} from '@heroicons/vue/24/outline';

const showVideoCallEndCallTooltip = ref(false);
const showVideoCallStartCallTooltip = ref(false);

const props = defineProps({
  open: Boolean
});

const emit = defineEmits(['start-call', 'update:open']);

// When the answer call button is clicked, emit the start-call event
const handleAnswerCall = () => {
  emit('start-call');
};

let ringtone = null;

// Initialize the audio element when the component is mounted
onMounted(() => {
  ringtone = new Audio(
    '/media/audio/platforma-video-call-incoming-call-sound.mp3'
  );
  ringtone.loop = true;
  if (props.open) {
    ringtone.play().catch(() => {
      // Autoplay policy prevents playing without user interaction.
      console.log('Autoplay was prevented.');
      // You might want to show some UI to the user to manually start the playback
    });
  }
});

onBeforeUnmount(() => {
  if (ringtone) {
    ringtone.pause();
    ringtone.src = ''; // This should help with garbage collection
    ringtone = null;
  }
});

const closeModal = () => {
  emit('update:open', false);
};

watchEffect(() => {
  if (props.open && ringtone) {
    ringtone.play().catch(() => {
      console.log('Playback was prevented. Waiting for user interaction.');
    });
  } else if (ringtone) {
    ringtone.pause();
    ringtone.currentTime = 0; // Rewind the audio
  }
});

// const startCall = () => {
//   emit('start-call');
//   closeModal();
// };
</script>

<style scoped>
/* This is for when the image overlay is opening */
/* image overlay transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from, .fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
.fade-enter-to {
  opacity: 1;
}
</style>
